/* @flow */

import memoize from 'lodash.memoize';
// $FlowFixMe
import BigNumber from 'bignumber.js';
import bog from './moreBog';

const DECIMAL_PLACES_MATCHER = /(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/;

export const DASH = '–';
const POSITIVE_SYMBOL = '+';
const NEGATIVE_SYMBOL = '-';

// used in formatNumberWithK
const K_THRESHOLD = 1000000;

// memoize for performance, avoids creating a RegExp each call
export const getFormatNumberRegexp = memoize((n, x) => new RegExp(
  `\\d(?=(\\d{${x || 3}})+${n > 0 ? '\\.' : '$'})`, 'g'),
(...args) => args.join(','));

export const getFormatNumberForIntegralRegexp = memoize((x) => new RegExp(
  `\\d(?=(\\d{${x || 3}})+$)`, 'g'));

export const equal = (x: number, y: number) => (Math.abs(x - y) < Number.EPSILON);

/**
 * Only format the integral part of a number with comma separated sections
 * whether it has the decimal part or not.
 *
 * ex. 111111.2222
 *     ^^^^^^
 *     integral part
 *     111111.2222
 *            ^^^^
 *     decimal part
 * @param num {number | string} num the number to format
 * @param x {integer} x length of sections
 * @returns {string}
 */
export const formatNumberForIntegralPart = (num: number | string = 0, x: ?number = 3) => {
  const re = getFormatNumberForIntegralRegexp(x);
  const [integral, decimal = ''] = `${num}`.split('.');
  return `${integral.replace(re, '$&,')}${decimal && `.${decimal}`}`;
};

/**
 * Format a number with comma separated sections.
 *
 * @param {number | string} num the number to format
 * @param {integer} n length of decimal
 * @param {integer} x length of sections
 */
export const formatNumber = (num: number | string = 0, n: number, x: ? number = 3) => {
  num = typeof num === 'number' ? num : +num;
  const re = getFormatNumberRegexp(n, x);
  return num.toFixed(Math.max(0, ~~n)).replace(re, '$&,');
};

export const formatBigNumber = (num: string, x?: number = 3) => {
  const [a = '0', b = '0'] = (`${num}`).split('.');
  const integerArr = a.split('');
  let decimalArr = b.split('');
  const diff = x - decimalArr.length;
  diff > 0 && (decimalArr = decimalArr.concat(new Array(diff).fill('0')));
  return `${integerArr.join('')}.${decimalArr.slice(0, x).join('')}`;
};
/**
 * Format a number with comma separated sections with "K" unit.
 *
 * @param {number | string} num the number to format
 * @param {integer} n length of decimal
 * @param {integer} x length of sections
 */
export const formatNumberWithK = (num: number | string, n: number, x: ? number =
3) => {
  num = typeof num === 'number' ? num : +num;
  return num >= K_THRESHOLD ? `${formatNumber(Math.round(num / 1000), 0)}K` :
    formatNumber(num, n, x);
};


/**
 * Add positive or negative indicator (+ or -) to a number.
 *
 * @param {number | string} num the number to format
 * @param {showPos} show plus `+` when positive?
 */
export const addPosNeg = (num: number | string, showPos ? : boolean = true) => !equal(+num, 0) && +num > 0 ? `${showPos ? '+' : ''}${num}` : `${num}`;

/**
 * Gets the rounding function for the type of trade.
 * See original code at https://git.io/vxJDj
 *
 * @param {string} type the type  of trade (bid, ask)
 */
export const getRoundFunc = (type: string = 'bids') => (() => {
  switch (type) {
    case 'bids':
      return 'floor';
    case 'asks':
      return 'ceil';
    default:
      if (Math[type]) return Math[type];
      throw new Error('getRoundFunc called with unknown type');
  }
})();

/**
 * Round a decimal number to the specified precision.
 *
 * @param {number | string} input
 * @param {number} precision
 * @param {function} func the rounding function
 * @param {bool} format should format?
 */
export const decRound = (
  input: number | string, precision: number, func: string = 'round', format ?
  : boolean = false
) => {
  const factor = 10 ** precision;
  // better browser support epsilon
  // see unit tests.
  const adj = 3;
  const decimal = String(input).split('.')[1] || '';
  const decimalLen = decimal.length > precision ? decimal.length : precision;
  let num = (+input + (1 / (10 ** (decimalLen + adj)))) * factor;

  if (typeof func === 'string') {
    const dps = adj - 1;
    num = parseInt(num * (10 ** dps), 10) / (10 ** dps);
    func = getRoundFunc(func);
  }
  if (typeof func !== 'function') {
    throw new Error(
      'decRound unknown rounding func');
  }
  if (format) {
    return formatNumber(func(num) / factor, precision);
  }
  return (func(num) / factor).toFixed(precision);
};

export const floor = (num: number | string, digit: number) => decRound(num,
  digit, 'floor');

export const ceil = (num: number | string, digit: number) => decRound(num,
  digit, 'ceil');

/**
 * Returns the number of decimal places in a number.
 *
 * @param {number | string} num
 */
export const decimalPlaces = (num: number | string) => {
  const match = `${num}`.match(DECIMAL_PLACES_MATCHER);
  if (!match) return 0;
  return Math.max(
    0,
    // Number of digits right of decimal point.
    (match[1] ? match[1].length : 0)
    // Adjust for scientific notation.
    -
    (match[2] ? +match[2] : 0));
};

export const multiply = (...args: number[]) => {
  const nums = args.map(n => Number(n));
  let factorsSum = 1;
  const factors = nums.map((n) => {
    const decimals = decimalPlaces(n);
    factorsSum *= 10 ** decimals;
    return (10 ** decimals) * n;
  });
  const product = factors.reduce((n, pre) => pre * n, 1);
  if (product > Number.MAX_SAFE_INTEGER) {
    bog.warn('multiply() fallback, The result might has some floating issue');
    return nums.reduce((n, pre) => n * pre, 1);
  }
  return product / factorsSum;
};

export const add = (arg1: number, arg2: number, noFixed: bool) => {
  let r1;
  let r2;
  try {
    r1 = arg1.toString().split('.')[1].length;
  } catch (e) {
    r1 = 0;
  }
  try {
    r2 = arg2.toString().split('.')[1].length;
  } catch (e) {
    r2 = 0;
  }
  const m = 10 ** Math.max(r1, r2);
  return ((arg1 * m + arg2 * m) / m).toFixed(noFixed ? Math.max(r1, r2) : 2);
};

export const subtract = (arg1: number, arg2: number) => {
  let r1;
  let r2;
  try {
    r1 = arg1.toString().split('.')[1].length;
  } catch (e) {
    r1 = 0;
  }
  try {
    r2 = arg2.toString().split('.')[1].length;
  } catch (e) {
    r2 = 0;
  }
  const m = 10 ** Math.max(r1, r2);
  return ((arg1 * m - arg2 * m) / m).toFixed(2);
};

/**
 * Returns the string with leading zeros.
 *
 * @param {number} num
 * @param {number} size
 * @return {string}
 */
export const pad = (num: number | string, size: number = 2) => {
  let str = String(num);
  while (str.length < size) {
    str = `0${str}`;
  }
  return str;
};

const getPencentageSymbol = (numerator, denominator) => {
  if (numerator > 0) {
    if (denominator > 0) {
      return POSITIVE_SYMBOL;
    }
    return NEGATIVE_SYMBOL;
  }
  if (numerator < 0) {
    if (denominator > 0) {
      return NEGATIVE_SYMBOL;
    }
    return POSITIVE_SYMBOL;
  }
  return '';
};

export const pencentage = (
  numerator: number, denominator: number, config: Object = {
    digit: 2,
    withSymbol: false,
    allowNegative: false,
  }
) => {
  const {
    digit = 2, withSymbol = false, allowNegative = false,
  } = config;
  const symbol = withSymbol ? getPencentageSymbol(numerator, denominator) : '';
  const diff = allowNegative ? numerator / denominator : Math.abs(numerator / denominator);
  return denominator === 0 ? DASH : `${symbol}${decRound(diff * 100, digit)}%`;
};

export const remainder = (num1: number, num2: number) => {
  const decimals1 = decimalPlaces(num1);
  const decimals2 = decimalPlaces(num2);
  const decimals = Math.max(decimals1, decimals2);
  return ((num1 * (10 ** decimals)) % (num2 * (10 ** decimals))) / (10 **
    decimals);
};

// 获取小数位数
const getPrecision = (num: string): number => {
  const result = /\.(\d*)/.exec(num.toString());
  if (result) {
    // $FlowFixMe
    return RegExp.$1.length;
  }
  return 0;
};

/**
 * 安全乘法
 *
 * @param string ...item
 * @return {string}
 */
export const safeMultiply = (...item: string[]): string => {
  let num1: string = '';
  let num2: string = '';
  if (item.length === 2) {
    [num1, num2] = item;
  } else {
    [num1] = item;
    item.shift();
    num2 = safeMultiply.apply(this, item);
  }
  // 小数位数
  const num1Precision: number = getPrecision(num1);
  const num2Precision: number = getPrecision(num2);
  const precision: number = num1Precision + num2Precision;

  // 负数情况
  let snegativeFlag: number = 0;
  num1 = /^-/.test(num1) ? (snegativeFlag++, num1.replace('-', '')) : num1;
  num2 = /^-/.test(num2) ? (snegativeFlag++, num2.replace('-', '')) : num2;

  const str1: string[] = num1.replace('.', '').split('').reverse();
  const str2: string[] = num2.replace('.', '').split('').reverse();
  const len1: number = str1.length;
  const len2: number = str2.length;
  const result: any[] = [];
  let resultLen = len1 + len2;
  for (; resultLen--;) {
    result[resultLen] = 0;
  }

  // 根据乘法规则计算
  for (let i = 0; i < len1; i++) {
    for (let j = 0; j < len2; j++) {
      result[i + j] += parseInt(str1[i], 0) * parseInt(str2[j], 0);
    }
  }
  resultLen = result.length;
  for (let i = 0; i < resultLen; i++) {
    const temp = result[i];
    if (temp >= 10) {
      result[i] = temp % 10;
      result[i + 1] += Math.floor(temp / 10);
    }
  }

  // 如果存在数位,那么插入小数点
  precision && result.splice(precision, 0, '.');
  let returnResult = result.reverse().join('');
  // 去除小数最后的 0
  if (precision) {
    returnResult = returnResult.replace(/0*$/, '');
  }
  // 去除整数前面的 0
  returnResult = returnResult.replace(/^0+/, '').replace(/^\./, '0.').replace(
    /\.$/, '');
  // 如果是负数,那加负号
  if (snegativeFlag === 1) {
    returnResult = `-${returnResult}`;
  }
  return returnResult || '0';
};

export const decMultiply = (decimal: number, ...args: number[]) => {
  const nums = args.map(n => String(n));
  return decRound(safeMultiply(...nums), decimal);
};

export const bigNumberGreaterThan = (a: string, b: string):bool => BigNumber(a).gt(b);
export const bigNumberGreaterThanOrEqual = (a: string, b: string):bool => BigNumber(a).gte(b);
export const bigNumberLessThanOrEqual = (a: string, b: string):bool => BigNumber(a).lte(b);
export const bigNumberEqual = (a: string, b: string):bool => BigNumber(a).eq(b);
export const bigNumberPlus = (a: string, b: string, fixedLen: number = 8):string => BigNumber(a).plus(b).toFixed(fixedLen);
export const bigNumberMinus = (a: string, b: string):string => BigNumber(a).minus(b).toString();
export const bigNumberMultiply = (num: string, base: string, fixedLen: number = 8) => BigNumber(num).multipliedBy(base).toFixed(fixedLen);
export const bigNumberDivided = (num: string, base: string, fixedLen: number = 8) => BigNumber(num).dividedBy(base).toFixed(fixedLen);

export default {
  getRoundFunc,
  decRound,
  formatNumber,
  decimalPlaces,
  pad,
  floor,
  ceil,
  decMultiply,
  multiply,
  remainder,
  add,
  subtract,
  safeMultiply,
};
